import {DopamineChatStreamEvent} from 'interfaces'
import {instance} from '../HTTP'

const MAX_SSE_FRAME_LENGTH = 1_000_000
const BROWSER_MOCK_EVENT_INTERVAL_MS = 800

type DopamineChatErrorCode = Extract<
  DopamineChatStreamEvent,
  {type: 'RUN_ERROR'}
>['code']

export type StreamChatOptions = {
  clientSessionId: string
  message: string
  onEvent(event: DopamineChatStreamEvent): void
  signal: AbortSignal
}

type StreamChatRequestOptions = StreamChatOptions & {
  baseUrl: string
}

const isBrowserMockEndpoint = (baseUrl: string): boolean => {
  if (process.env.REACT_APP_ENABLE_MSW !== 'true') return false

  try {
    return new URL(baseUrl).hostname.endsWith('.msw.invalid')
  } catch {
    return false
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeChatErrorCode = (code: string): DopamineChatErrorCode | null => {
  switch (code) {
    case 'AGENT_ERROR':
    case 'INTERNAL_ERROR':
    case 'SESSION_EXPIRED':
      return code
    // The deployed producer has not adopted the public schema names yet.
    case 'CHAT_EXPIRED':
      return 'SESSION_EXPIRED'
    case 'MISSING_REGION':
      return 'INTERNAL_ERROR'
    default:
      return null
  }
}

const readString = (record: Record<string, unknown>, key: string): string => {
  const value = record[key]
  if (typeof value !== 'string') {
    throw new TypeError(`Invalid Dopamine chat event field: ${key}`)
  }
  return value
}

const parseChatEvent = (data: string): DopamineChatStreamEvent => {
  let value: unknown
  try {
    value = JSON.parse(data)
  } catch {
    throw new Error('Invalid Dopamine chat event JSON')
  }

  if (!isRecord(value)) {
    throw new Error('Invalid Dopamine chat event')
  }

  const event = value
  const type = readString(event, 'type')

  switch (type) {
    case 'RUN_STARTED':
    case 'RUN_FINISHED':
      return {
        runId: readString(event, 'runId'),
        threadId: readString(event, 'threadId'),
        type,
      }
    case 'RUN_ERROR': {
      const code = normalizeChatErrorCode(readString(event, 'code'))
      if (code === null) {
        throw new Error('Invalid Dopamine chat error code')
      }
      return {
        code,
        message: readString(event, 'message'),
        type,
      }
    }
    case 'TEXT_MESSAGE_START':
      if (readString(event, 'role') !== 'assistant') {
        throw new Error('Invalid Dopamine chat message role')
      }
      return {
        messageId: readString(event, 'messageId'),
        role: 'assistant',
        type,
      }
    case 'TEXT_MESSAGE_CONTENT':
      return {
        delta: readString(event, 'delta'),
        messageId: readString(event, 'messageId'),
        type,
      }
    case 'TEXT_MESSAGE_END':
      return {messageId: readString(event, 'messageId'), type}
    case 'STEP_STARTED':
    case 'STEP_FINISHED':
      return {
        displayName: readString(event, 'displayName'),
        stepName: readString(event, 'stepName'),
        type,
      }
    case 'TOOL_CALL_START': {
      const parentMessageId = event.parentMessageId
      if (
        parentMessageId !== undefined &&
        typeof parentMessageId !== 'string'
      ) {
        throw new Error('Invalid Dopamine parent message ID')
      }
      return {
        displayName: readString(event, 'displayName'),
        parentMessageId,
        toolCallId: readString(event, 'toolCallId'),
        toolCallName: readString(event, 'toolCallName'),
        type,
      }
    }
    case 'TOOL_CALL_END':
      return {toolCallId: readString(event, 'toolCallId'), type}
    case 'TOOL_CALL_RESULT':
      if (readString(event, 'role') !== 'tool') {
        throw new Error('Invalid Dopamine tool result role')
      }
      return {
        content: readString(event, 'content'),
        messageId: readString(event, 'messageId'),
        role: 'tool',
        toolCallId: readString(event, 'toolCallId'),
        type,
      }
    default:
      throw new Error(`Unsupported Dopamine chat event: ${type}`)
  }
}

const consumeFrame = (
  frame: string,
  onEvent: StreamChatOptions['onEvent'],
): void => {
  if (frame.length > MAX_SSE_FRAME_LENGTH) {
    throw new Error('Dopamine chat event is too large')
  }

  const data = frame
    .replace(/\r\n|\r/g, '\n')
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).trimStart())
    .join('\n')

  if (data) onEvent(parseChatEvent(data))
}

const createStreamCanceledError = (): Error =>
  new Error('Dopamine chat stream was canceled')

const consumeSSE = async (
  stream: ReadableStream<Uint8Array>,
  onEvent: StreamChatOptions['onEvent'],
  signal: AbortSignal,
): Promise<void> => {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let cancelPromise: Promise<void> | null = null
  let buffer = ''
  let streamComplete = false

  const cancelReader = (): Promise<void> => {
    cancelPromise ??= reader.cancel().catch(() => undefined)
    return cancelPromise
  }
  const handleAbort = (): void => {
    void cancelReader()
  }
  const throwIfCanceled = (): void => {
    if (signal.aborted) throw createStreamCanceledError()
  }

  signal.addEventListener('abort', handleAbort, {once: true})

  try {
    throwIfCanceled()
    while (!streamComplete) {
      const {done, value} = await reader.read()
      throwIfCanceled()
      streamComplete = done
      buffer += decoder.decode(value, {stream: !done})
      const hasPartialCarriageReturn = !done && buffer.endsWith('\r')
      const completeBuffer = hasPartialCarriageReturn
        ? buffer.slice(0, -1)
        : buffer
      buffer = `${completeBuffer.replace(/\r\n|\r/g, '\n')}${
        hasPartialCarriageReturn ? '\r' : ''
      }`

      let frameEnd = buffer.indexOf('\n\n')
      while (frameEnd !== -1) {
        throwIfCanceled()
        consumeFrame(buffer.slice(0, frameEnd), onEvent)
        buffer = buffer.slice(frameEnd + 2)
        frameEnd = buffer.indexOf('\n\n')
      }

      if (buffer.length > MAX_SSE_FRAME_LENGTH) {
        throw new Error('Dopamine chat event is too large')
      }
    }

    if (buffer.trim()) {
      throwIfCanceled()
      consumeFrame(buffer, onEvent)
    }
  } catch (error) {
    await cancelReader()
    throw error
  } finally {
    signal.removeEventListener('abort', handleAbort)
    reader.releaseLock()
  }
}

const waitForBrowserMockEvent = (signal: AbortSignal): Promise<void> => {
  if (process.env.NODE_ENV === 'test') return Promise.resolve()
  if (signal.aborted) return Promise.reject(createStreamCanceledError())

  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const handleAbort = (): void => {
      if (timeoutId !== null) clearTimeout(timeoutId)
      signal.removeEventListener('abort', handleAbort)
      reject(createStreamCanceledError())
    }

    signal.addEventListener('abort', handleAbort, {once: true})
    if (signal.aborted) {
      handleAbort()
      return
    }

    timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, BROWSER_MOCK_EVENT_INTERVAL_MS)
  })
}

const consumeSSEText = async (
  body: string,
  onEvent: StreamChatOptions['onEvent'],
  signal: AbortSignal,
  paceEvents: boolean,
): Promise<void> => {
  const frames = body
    .replace(/\r\n|\r/g, '\n')
    .split('\n\n')
    .filter(frame => frame.trim())

  for (const [index, frame] of frames.entries()) {
    if (signal.aborted) throw createStreamCanceledError()
    consumeFrame(frame, onEvent)

    if (paceEvents && index < frames.length - 1) {
      await waitForBrowserMockEvent(signal)
    }
  }
}

export const streamChatRequest = async ({
  baseUrl,
  clientSessionId,
  message,
  onEvent,
  signal,
}: StreamChatRequestOptions): Promise<void> => {
  const isBrowserMock = isBrowserMockEndpoint(baseUrl)
  // MSW v1 bypasses requests that advertise SSE and buffers mock responses.
  // Real agent traffic still uses fetch so chunks render as they arrive.
  const supportsFetchStream =
    !isBrowserMock &&
    typeof fetch === 'function' &&
    typeof ReadableStream !== 'undefined'
  const response = await instance.post<ReadableStream<Uint8Array> | string>(
    `${baseUrl}/chat`,
    {data: {message}},
    {
      adapter: supportsFetchStream ? 'fetch' : undefined,
      headers: {
        ...(!isBrowserMock && {Accept: 'text/event-stream'}),
        'X-Client-Session-Id': clientSessionId,
      },
      responseType: supportsFetchStream ? 'stream' : 'text',
      signal,
    },
  )
  const contentType = response.headers['content-type']

  if (
    typeof contentType !== 'string' ||
    !contentType.includes('text/event-stream')
  ) {
    throw new Error('Dopamine chat response is not an SSE stream')
  }
  if (typeof response.data === 'string') {
    await consumeSSEText(response.data, onEvent, signal, isBrowserMock)
    return
  }
  if (!response.data || typeof response.data.getReader !== 'function') {
    throw new Error('Dopamine chat response has no stream body')
  }

  await consumeSSE(response.data, onEvent, signal)
}
